import React, { useState } from 'react';
import { Button, Card, ConfirmModal, Panel, Table } from '@/components/Bicester';
import { ColumnConfig } from '@/components/Bicester/Table';
import { Alert } from 'rsuite';
import { FormikProvider, useFormik } from 'formik';
import { defaultInfo } from './constants';
import { Form } from '@/components/formik-rsuite';
import { IconButton } from 'rsuite';
import Image from '@/components/Bicester/Image';
import useContentHeight from '@/hooks/useContentHeight';
import {
  DateTimeCell,
  UserNameCell,
  EditableCell,
  Cell,
  OperationCell,
  LevelCell,
} from '@/components/Bicester/Table/Cells';
import { BICESTER_FULL_DATE } from '@/constants/dateFormat';
import { usePersistFn, useRequest } from 'ahooks';
import { InsideCell } from './Cell';
import {
  fetchList,
  fetchTimeDate,
  fetchCheckIn,
  fetchCheckOut,
  fetchUpdate,
  fetchRevokeIn,
  fetchNoShowVip,
  fetchRevokeNoShowVip,
} from '@/services/subscribe/lounge';
import { Bicester } from '@/constants';
import _ from 'lodash';
import { useTable } from '@/hooks';
import { DEFAULT_TABLE_VALUE } from '@/constants/config';
import { useReadyInfo } from '@/providers/ReadyProvider';
import { SERVICE_STATUS } from '@/services/constants';
import { fetchSubscribeSurplus } from '@/services/subscribe';
import { getSubscribeSurplusCount, onChangeRowData } from '../utils';
import { SUBSCRIBE_OPTION } from '../constants';
import { LAYOUT_CONFIG } from '@/constants/layoutConfig';
import { useModal } from '@ebay/nice-modal-react';

const Lounge = () => {
  const { profile } = useReadyInfo();
  const modal = useModal(ConfirmModal);

  const [data, setData] = useState([]);

  // 预约时间段
  const { data: timeDates } = useRequest(fetchTimeDate, {
    formatResult(response) {
      return response.map(({ number, time_slot }) => ({
        label: time_slot,
        value: number,
      }));
    },
  });

  const {
    run: fetchTables,
    data: { current_page, total },
    loading: getListLoading,
  } = useRequest<typeof DEFAULT_TABLE_VALUE>(fetchList, {
    defaultParams: _.omit(_.pickBy(defaultInfo), 'time_range'),
    initialData: DEFAULT_TABLE_VALUE,
    onSuccess: (res) => setData(res.content_list),
  });

  const { run: getSurplusCount, loading: getSurplusCountLoading } = useRequest(
    fetchSubscribeSurplus,
    { manual: true }
  );

  const { onChange, onSearch, onRefresh } = useTable(fetchTables, {
    start_at: defaultInfo.time_range[0],
    end_at: defaultInfo.time_range[1],
  });

  const formik = useFormik({
    initialValues: defaultInfo,
    enableReinitialize: true,
    onSubmit(values) {
      const { time_range = [], number, ...rest } = values;
      const params = {
        ...rest,
        start_at: time_range[0] || '',
        end_at: time_range[1] || '',
        number: number?.join?.() || '',
      };
      onSearch(_.pickBy(params));
    },
  });

  const operationHandle = usePersistFn((rowData) => {
    const { isEdit, id, hideBtn, content } = rowData;
    if (!rowData?.check_out_time) {
      const name = isEdit ? '保存' : '编辑';
      const onClick = async () => {
        if (isEdit) {
          if (!content) {
            Alert.error('到场人数不能为空');
            return;
          } else {
            const { status } = await fetchUpdate(_.pick(rowData, ['id', 'content', 'remark']));
            if (status === SERVICE_STATUS.SUCCESS) {
              onRefresh();
              Alert.success('修改成功');
            }
          }
        } else {
          getSubscribeSurplusCount(getSurplusCount, rowData, SUBSCRIBE_OPTION.LOUNGE, setData);
        }
        const nextData = data?.map((item) => {
          if (item.id === id) {
            return { ...item, isEdit: !item?.isEdit };
          } else {
            return { ...item, hideBtn: !item?.hideBtn };
          }
        });
        setData(nextData);
      };
      return [{ name, onClick, show: !hideBtn }];
    }
    return [{ name: '', readonly: true }];
  });

  const insideHandle = usePersistFn(async (rowData) => {
    const { status } = await fetchCheckIn({ id: rowData?.id });
    if (status === SERVICE_STATUS.SUCCESS) {
      onRefresh();
      Alert.success('入场成功');
    }
  });
  const revokeInsideHandle = usePersistFn(async (rowData) => {
    const { status } = await fetchRevokeIn({ id: rowData?.id });
    if (status === SERVICE_STATUS.SUCCESS) {
      onRefresh();
      Alert.success('撤销成功');
    }
  });
  const outSideHandle = usePersistFn(async (rowData) => {
    const { status } = await fetchCheckOut({ id: rowData?.id });
    if (status === SERVICE_STATUS.SUCCESS) {
      onRefresh();
      Alert.success('出场成功');
    }
  });

  const columns: ColumnConfig[] = [
    {
      columnProps: { flexGrow: 0.9 },
      header: '预约服务日期',
      cell: <DateTimeCell dataKey="date" format={BICESTER_FULL_DATE} />,
    },
    {
      columnProps: { flexGrow: 0.9 },
      header: '预约时间段',
      cell: <Cell dataKey="time_slot" />,
    },
    {
      columnProps: { flexGrow: 0.6 },
      header: '客户名',
      cell: <UserNameCell dataKey="user_name" />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '手机号',
      cell: <Cell dataKey="phone" />,
    },
    {
      columnProps: { flexGrow: 0.6 },
      header: '会员等级',
      cell: <LevelCell dataKey="level" />,
    },
    {
      columnProps: { flexGrow: 0.6 },
      header: '到场人数',
      cell: (
        <EditableCell
          editComponent="inputNumber"
          dataKey="content"
          setState={setData}
          autoFill={false}
          max={100}
          min={1}
          after="人"
        />
      ),
    },
    {
      columnProps: { flexGrow: 1 },
      header: '入场时间',
      cell: (
        <InsideCell
          dataKey="write_off_time"
          content="确认入场"
          handle={insideHandle}
          revokeHandle={revokeInsideHandle}
          isHide={(rowData) => rowData?.isEdit}
        />
      ),
    },
    {
      columnProps: { flexGrow: 1 },
      header: '出场时间',
      cell: (
        <InsideCell
          dataKey="check_out_time"
          content="确认出场"
          handle={outSideHandle}
          isHide={(rowData) => rowData?.isEdit || !rowData?.write_off_time}
        />
      ),
    },
    {
      columnProps: { flexGrow: 0.8 },
      header: 'No show',
      cell: (
        <Cell>
          {(rowData) => {
            /**
             * 已入场和未入场的noshow状态，显示红色文案加撤销按钮
             * 已入场，非noshow，什么都不显示
             * 未入场，非noshow状态，显示noshow按钮
             */
            const isNoShow = _.get(rowData, 'no_show') === 1; // 是否noshow状态
            const isEntered = !!_.get(rowData, 'write_off_time'); // 已入场
            if (isEntered && !isNoShow) return null; // 已入场，非noshow，什么都不显示
            return (
              <div className="flex">
                {/* noshow状态，显示红色noshow文案和撤销按钮 */}
                {isNoShow && <span style={{ color: 'red' }}>No show</span>}
                <Button
                  appearance="link"
                  onClick={() => {
                    modal.show({
                      contentText: `是否确认${!isEntered && !isNoShow ? 'No show' : '撤销'}？`,
                      submitText: '确认',
                      cancelText: '取消',
                      onSubmit: async () => {
                        if (isNoShow) {
                          const { status } = await fetchRevokeNoShowVip({ id: rowData?.id });
                          if (status === SERVICE_STATUS.SUCCESS) {
                            onRefresh();
                            Alert.success('撤销成功');
                          }
                        } else {
                          const { status } = await fetchNoShowVip({ id: rowData?.id });
                          if (status === SERVICE_STATUS.SUCCESS) {
                            onRefresh();
                            Alert.success('操作成功');
                          }
                        }
                      },
                    });
                  }}
                  style={{ color: '#73D8DA', marginLeft: isNoShow ? 5 : 0 }}
                >
                  {!isEntered && !isNoShow ? 'No show' : '撤销'}
                </Button>
              </div>
            );
          }}
        </Cell>
      ),
    },
    {
      columnProps: { flexGrow: 1 },
      header: '桌号',
      cell: <EditableCell dataKey="remark" setState={setData} />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '操作',
      cell: <OperationCell operations={operationHandle} />,
    },
  ];

  return (
    <div>
      <Panel dividerHeight={10} leftShadow title="贵宾休息室预约查询">
        <FormikProvider value={formik}>
          <Form>
            <Card smDivider>
              <div className="inline-flex w-[95%] flex-1">
                <Card.Input label="手机号" formKey="phone" labelWeight={3} />
                <Card.Input label="会员号" formKey="user_code" labelWeight={3} />
                <Card.Picker
                  label="会员等级"
                  formKey="level"
                  pickerData={[
                    { label: '奕尊', value: 3 },
                    { label: '奕尚', value: 4 },
                  ]}
                  labelWeight={3}
                />
                <Card.DateRangePicker label="预约日期" formKey="time_range" labelWeight={3} />
                <Card.CheckPicker
                  label="预约时间段"
                  formKey="number"
                  pickerData={timeDates}
                  labelWeight={3}
                />
              </div>
              <IconButton type="submit" className="inline-flex items-center w-[30px] ml-[10px]">
                <Image src="/resources/images/match.svg" />
              </IconButton>
            </Card>
          </Form>
        </FormikProvider>
      </Panel>
      <Panel
        title={`${Bicester[profile?.shopping_village]} | 贵宾休息室预约`}
        subTitle={`总计${total}条`}
        leftShadow
        style={{
          height:
            'max(calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight) - 169px), auto)',
        }}
      >
        <Table
          loading={getListLoading || getSurplusCountLoading}
          style={{
            height: `max(${
              useContentHeight({
                withToolBar: true,
                withPanelHeader: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) - 169
            }px, ${
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT +
              LAYOUT_CONFIG.TABLE_PAGINATION_WITH_MARGIN_HEIGHT
            }px})`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          pageInfo={{
            page: current_page,
            total,
          }}
          className=""
          onChange={onChange}
          data={data}
          columns={columns}
        >
          <Table.Body
            height={Math.max(
              useContentHeight({
                withToolBar: true,
                withPanelHeader: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) - 169,
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT
            )}
          />
          <Table.Pagination />
        </Table>
      </Panel>
    </div>
  );
};

export default Lounge;
